/**
 * Scenarios: the vocabulary every simulation test is written in.
 *
 * A scenario is a declarative object — map seed, player slots, and a list of
 * `{tick, slot, command}` entries — that the runner executes to a target tick
 * and returns final state plus a hash trace from. Every later simulation unit
 * tests in this vocabulary, so it is worth keeping terse to author.
 */

import { Sim, type MatchDefinition, type PlayerSlot } from './index.js'
import type { Command } from '../net/commands.js'
import { LocalRelay } from '../net/local.js'
import { hashHex } from '../core/hash.js'
import { HASH_INTERVAL } from '../core/config.js'

/** A command to submit at a given tick. `slot` defaults to the command's own. */
export interface ScheduledCommand {
  tick: number
  slot?: number
  command: Omit<Command, 'slot' | 'seq'> & { slot?: number }
}

export interface Scenario {
  name: string
  mapSeed: number | string
  seed?: number | string
  slots?: PlayerSlot[]
  /** Tick to run to, exclusive. */
  ticks: number
  commands?: ScheduledCommand[]
  hashInterval?: number
  /** Runs once after setup, before tick 0. Used to place test entities. */
  setup?: (sim: Sim) => void
  /** Runs after every executed tick. Used for assertions and instrumentation. */
  onTick?: (sim: Sim, tick: number) => void
}

export interface ScenarioResult {
  name: string
  ticksRun: number
  finalHash: number
  /** Checkpoint hashes, `[tick, hash]` pairs. */
  trace: Array<{ tick: number; hash: number }>
  stalled: boolean
  sim: Sim
}

export class ScenarioAuthoringError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ScenarioAuthoringError'
  }
}

export function defaultSlots(n = 2): PlayerSlot[] {
  return Array.from({ length: n }, (_, i) => ({ faction: i % 2, team: i }))
}

/**
 * Slots for a skirmish: one human seat, every other seat an AI.
 *
 * `defaultSlots` marks nobody, because a scenario or a test usually wants an
 * inert opponent. The *game* does not, and shipped without an opponent at all:
 * the AI attaches to slots whose `kind` is `'ai'`, the browser build never set
 * it, and a single-player match was a player alone on a map with an enemy base
 * that never did anything.
 *
 * The bot-match sweep could not have caught it — it builds its own slots and
 * marks them itself, so it was testing the AI and never the wiring the player
 * actually gets.
 */
export function skirmishSlots(count = 2, localSlot = 0, difficulty = 1): PlayerSlot[] {
  return defaultSlots(count).map((slot, i) => ({
    ...slot,
    kind: i === localSlot ? 'human' : 'ai',
    ...(i === localSlot ? {} : { difficulty }),
  }))
}

export function buildMatch(scenario: Scenario): MatchDefinition {
  return {
    mapSeed: scenario.mapSeed,
    seed: scenario.seed ?? scenario.mapSeed,
    slots: scenario.slots ?? defaultSlots(2),
  }
}

/**
 * Execute a scenario to completion.
 *
 * A command scheduled past the scenario's end raises an authoring error rather
 * than being silently dropped — a test that quietly never runs its own input is
 * worse than one that fails.
 */
export function runScenario(scenario: Scenario): ScenarioResult {
  const sim = Sim.create({
    match: buildMatch(scenario),
    localSlot: 0,
    hashInterval: scenario.hashInterval ?? HASH_INTERVAL,
  })

  scenario.setup?.(sim)

  const pending = (scenario.commands ?? []).slice()
  for (const c of pending) {
    if (c.tick < 0 || c.tick >= scenario.ticks) {
      throw new ScenarioAuthoringError(
        `scenario '${scenario.name}': command scheduled at tick ${c.tick}, ` +
          `outside the scenario's range [0, ${scenario.ticks})`,
      )
    }
  }
  pending.sort((a, b) => a.tick - b.tick || (a.slot ?? 0) - (b.slot ?? 0))

  const relay = sim.relay as LocalRelay
  let next = 0
  let ticksRun = 0
  let stalled = false

  for (let tick = 0; tick < scenario.ticks; tick++) {
    // Submissions made "during" tick N execute at N + 1 + delay.
    while (next < pending.length && pending[next].tick === tick) {
      const entry = pending[next++]
      const slot = entry.slot ?? entry.command.slot ?? 0
      relay.submit({ ...entry.command, slot } as never)
    }
    if (!sim.step()) {
      stalled = true
      break
    }
    ticksRun++
    scenario.onTick?.(sim, tick)
  }

  return {
    name: scenario.name,
    ticksRun,
    finalHash: sim.currentHash(),
    trace: sim.engine.hashTrace.slice(),
    stalled,
    sim,
  }
}

// ---------------------------------------------------------------------------
// Recorded logs (R5)
// ---------------------------------------------------------------------------

/** A replayable match record: seed, map, and the command log. */
export interface MatchRecord {
  version: 1
  mapSeed: number | string
  seed: number | string
  slots: PlayerSlot[]
  ticks: number
  commands: ScheduledCommand[]
}

export function scenarioFromRecord(record: MatchRecord, name = 'replay'): Scenario {
  if (record.version !== 1) {
    throw new ScenarioAuthoringError(`unsupported record version ${record.version}`)
  }
  return {
    name,
    mapSeed: record.mapSeed,
    seed: record.seed,
    slots: record.slots,
    ticks: record.ticks,
    commands: record.commands,
  }
}

/**
 * Compare two hash traces, reporting the **first** diverging tick rather than
 * merely that they differ. Which tick diverged is the whole diagnostic value.
 */
export interface TraceComparison {
  identical: boolean
  divergedAt: number | null
  /** The checkpoint interval containing the divergence, as `[from, to]`. */
  window: [number, number] | null
  detail: string
}

export function compareTraces(
  a: Array<{ tick: number; hash: number }>,
  b: Array<{ tick: number; hash: number }>,
  labelA = 'A',
  labelB = 'B',
): TraceComparison {
  const n = Math.min(a.length, b.length)
  for (let i = 0; i < n; i++) {
    if (a[i].tick !== b[i].tick || a[i].hash !== b[i].hash) {
      const from = i > 0 ? a[i - 1].tick : 0
      const to = a[i].tick
      return {
        identical: false,
        divergedAt: a[i].tick,
        window: [from, to],
        detail:
          `first divergence at checkpoint tick ${a[i].tick} ` +
          `(window ${from}-${to}): ${labelA}=${hashHex(a[i].hash)} ${labelB}=${hashHex(b[i].hash)}`,
      }
    }
  }
  if (a.length !== b.length) {
    const shorter = a.length < b.length ? labelA : labelB
    const lastTick = n > 0 ? a[n - 1].tick : 0
    return {
      identical: false,
      divergedAt: lastTick,
      window: [lastTick, lastTick],
      detail: `${shorter} stopped early: last verified tick ${lastTick}`,
    }
  }
  return { identical: true, divergedAt: null, window: null, detail: `${n} checkpoints identical` }
}
